// src/pages/CreateOrganization.js

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, setDoc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';
import { BuildingOfficeIcon, UserIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const PLATFORM_TIERS = [
  { id: 'starter', name: 'Starter', range: 'Pilot / Getting started', priceMonthly: 49, priceAnnual: 490 },
  { id: 'standard', name: 'Standard', range: 'Established churches', priceMonthly: 99, priceAnnual: 990 },
];

const CreateOrganization = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, setCurrentUser } = useAuth();

  // Pre-selected tier from For Churches page (e.g. ?tier=small-pilot)
  const tierParam = searchParams.get('tier');

  // Step tracking: 'org-name' → 'personal-info' → 'church-plan' → 'org-details'
  // Authenticated users skip straight to 'org-details'
  const [step, setStep] = useState('loading');

  // Organization fields
  const [orgName, setOrgName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [country, setCountry] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [website, setWebsite] = useState('');
  const [settings, setSettings] = useState({
    primaryColor: '#2563eb',
    allowSelfJoin: false,
    customDomain: '',
    defaultRole: 'Discipler',
    storySettings: {
      autoApprove: false,
      allowMemberStories: true
    }
  });

  // Personal account fields (for unauthenticated users)
  const [personalName, setPersonalName] = useState('');
  const [personalEmail, setPersonalEmail] = useState('');
  const [personalPhone, setPersonalPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);

  // Church size plan (platform tier) — pre-select from URL if valid
  const [selectedPlatformTier, setSelectedPlatformTier] = useState(() => {
    if (tierParam && PLATFORM_TIERS.some((t) => t.id === tierParam)) return tierParam;
    return null;
  });

  // Determine initial step based on auth state
  useEffect(() => {
    if (currentUser) {
      // Authenticated — fetch user data and go to church plan step
      const fetchUserData = async () => {
        try {
          const userRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            setUserData(userDoc.data());
          }
        } catch (err) {
          console.error('Error fetching user data:', err);
        }
        setStep('church-plan');
      };
      fetchUserData();
    } else {
      // Not authenticated — start with org name step
      setStep('org-name');
    }
  }, [currentUser]);

  // ─── Helpers for account creation ────────────────────────────────

  const generateVanityAppId = async () => {
    const counterRef = doc(db, 'counters', 'userCounter');
    return await runTransaction(db, async (transaction) => {
      const counterSnap = await transaction.get(counterRef);
      const newCount = (counterSnap.exists() ? counterSnap.data().count : 0) + 1;
      transaction.set(counterRef, { count: newCount }, { merge: true });
      return `UID${String(newCount).padStart(3, '0')}`;
    });
  };

  const generateStoryId = async () => {
    const counterRef = doc(db, 'counters', 'storyCounter');
    return await runTransaction(db, async (transaction) => {
      const counterSnap = await transaction.get(counterRef);
      const newCount = (counterSnap.exists() ? counterSnap.data().count : 0) + 1;
      transaction.set(counterRef, { count: newCount }, { merge: true });
      return `storyID_${String(newCount).padStart(5, '0')}`;
    });
  };

  // ─── Step 1 → Step 2: Org Name → Personal Info ──────────────────

  const handleOrgNameNext = (e) => {
    e.preventDefault();
    setError(null);
    setStep('personal-info');
  };

  // ─── Step 2 → Step 3: Create Account → Org Details ──────────────

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    setIsSubmitting(true);

    try {
      const auth = getAuth();
      const userCredential = await createUserWithEmailAndPassword(auth, personalEmail, password);
      const user = userCredential.user;

      const appID = await generateVanityAppId();
      const selectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const newUserData = {
        uid: user.uid,
        email: user.email,
        name: personalName || 'User',
        phone: personalPhone || '',
        photo: '',
        role: '/roles/discipler',
        status: 'Active',
        groups: [],
        discipleIds: [],
        archivedDiscipleIds: [],
        activeOrgs: [],
        createdAt: serverTimestamp(),
        appID,
        phoneVerified: false,
        phoneVerificationId: null,
        lastVerificationAttempt: null,
        isNewUser: true,
        onboardingComplete: false,
        onboardingStep: 1,
        notificationPreferences: {
          push: { enabled: true, permission: 'default' },
          email: { enabled: true, verified: false },
          sms: { enabled: false, verified: false },
          quietHours: { enabled: true, start: '22:00', end: '08:00' },
        },
        timezone: selectedTimezone,
      };

      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, newUserData);

      // Generate welcome story
      const storyId = await generateStoryId();
      const userStoryRef = doc(db, 'users', user.uid, 'stories', storyId);
      await setDoc(userStoryRef, {
        text: 'Welcome to Together on Mission! This is your private story space where you can document your mission journey.',
        authorId: user.uid,
        authorName: personalName || 'User',
        authorEmail: user.email,
        authorPhotoURL: '',
        metrics: {
          spiritualDiscussions: 0,
          heardGospel: 0,
          experiencedLove: 0,
          believersEncouraged: 0,
          newFollowers: 0,
        },
        sharing: { private: true, organizations: [] },
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
        status: 'active',
        id: storyId,
      });

      // Update auth context
      setUserData(newUserData);
      if (setCurrentUser) {
        setCurrentUser({ ...user, ...newUserData });
      }

      // Move to church plan step — user is now authenticated
      setStep('church-plan');
    } catch (err) {
      console.error('Error creating account:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already in use. Please log in first, then create your organization.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Step: Church Plan → Org Details ──────────────────────────────

  const handleChurchPlanNext = (e) => {
    e?.preventDefault();
    if (!selectedPlatformTier) {
      setError('Please select a church size plan.');
      return;
    }
    setError(null);
    setStep('org-details');
  };

  // ─── Final Step: Create the Organization ─────────────────────────

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // At this point currentUser should be set (either already was, or just created)
    const user = currentUser || getAuth().currentUser;
    const uData = userData;

    if (!user) {
      setError('You must be signed in to create an organization.');
      setIsSubmitting(false);
      return;
    }

    if (!selectedPlatformTier) {
      setError('Please select a church size plan.');
      setIsSubmitting(false);
      return;
    }

    try {
      const orgRef = doc(collection(db, 'organizations'));
      const orgData = {
        name: orgName,
        displayName: displayName || orgName,
        bio,
        selectedPlatformTier,
        vitals: {
          country,
          zipCode,
          website: website || null
        },
        createdBy: {
          uid: user.uid,
          appID: uData?.appID,
          email: user.email,
          name: uData?.name || personalName || 'Unknown User'
        },
        settings,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        status: 'pending',
        memberCount: 1,
        groupCount: 0,
        lastActivityAt: serverTimestamp(),
        metrics: {
          totalMembers: 1,
          activeMembers: 1,
          totalGroups: 0,
          activeGroups: 0,
          totalStories: 0,
          approvedStories: 0
        },
        storyFeed: {
          enabled: true,
          lastUpdated: serverTimestamp()
        }
      };

      await setDoc(orgRef, orgData);

      // Add creator to organization's users subcollection
      const orgUserRef = doc(db, 'organizations', orgRef.id, 'users', user.uid);
      try {
        await setDoc(orgUserRef, {
          uid: user.uid,
          appID: uData?.appID,
          email: user.email,
          name: uData?.name || personalName || 'Unknown User',
          role: 'Admin',
          status: 'active',
          joinedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp(),
          isCreator: true,
          privacySettings: {
            shareEmail: true,
            sharePhone: false,
            shareStories: true
          }
        });
      } catch (userError) {
        console.error('Error adding creator to users subcollection:', userError);
      }

      // Add creator to members subcollection (used by security rules)
      const orgMemberRef = doc(db, 'organizations', orgRef.id, 'members', user.uid);
      try {
        await setDoc(orgMemberRef, {
          uid: user.uid,
          email: user.email,
          name: uData?.name || personalName || 'Unknown User',
          role: 'Admin',
          status: 'active',
          joinedAt: serverTimestamp(),
          isCreator: true,
        });
      } catch (memberError) {
        console.error('Error adding creator to members subcollection:', memberError);
      }

      // Add organization to user's organizations subcollection
      const userOrgRef = doc(db, 'users', user.uid, 'organizations', orgRef.id);
      await setDoc(userOrgRef, {
        orgId: orgRef.id,
        orgName: orgName,
        role: 'Admin',
        status: 'pending',
        joinedAt: serverTimestamp(),
        lastActivityAt: serverTimestamp(),
        isCreator: true,
        settings: {
          notifications: true,
          shareStories: true
        }
      });

      // Create default group (non-critical — don't block org creation)
      try {
        const defaultGroupRef = doc(collection(db, 'organizations', orgRef.id, 'groups'));
        await setDoc(defaultGroupRef, {
          name: 'General',
          description: 'Default group for all members',
          createdAt: serverTimestamp(),
          createdBy: {
            uid: user.uid,
            email: user.email,
            name: uData?.name || personalName || 'Unknown User'
          },
          status: 'active',
          isDefault: true,
          memberCount: 1
        });

        const groupUserRef = doc(db, 'organizations', orgRef.id, 'groups', defaultGroupRef.id, 'users', user.uid);
        await setDoc(groupUserRef, {
          uid: user.uid,
          email: user.email,
          name: uData?.name || personalName || 'Unknown User',
          role: 'Admin',
          status: 'active',
          joinedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp()
        });

        const userGroupRef = doc(db, 'users', user.uid, 'groups', defaultGroupRef.id);
        await setDoc(userGroupRef, {
          organizationId: orgRef.id,
          role: 'Admin',
          status: 'active',
          joinedAt: serverTimestamp(),
          lastActivityAt: serverTimestamp()
        });
      } catch (groupError) {
        console.warn('Could not create default group (non-critical):', groupError);
      }

      // System notification
      const systemMessageRef = doc(collection(db, 'users', user.uid, 'notifications'));
      await setDoc(systemMessageRef, {
        type: 'organizationPending',
        title: 'Organization Application Submitted',
        message: `Your application for "${orgName}" has been submitted and is pending approval.`,
        orgId: orgRef.id,
        createdAt: serverTimestamp(),
        read: false,
        action: {
          text: 'View Organization',
          link: `/organization/${orgRef.id}`
        }
      });

      // Initialize stories collection
      const storiesCollectionRef = collection(db, 'organizations', orgRef.id, 'stories');
      const dummyStoryRef = doc(storiesCollectionRef, '_collection_init');
      await setDoc(dummyStoryRef, {
        _type: 'collection_init',
        createdAt: serverTimestamp(),
        note: 'This document initializes the stories collection. It can be safely deleted.'
      });

      navigate('/dashboard', {
        state: {
          notification: {
            type: 'success',
            message: 'Organization application submitted successfully! You will be notified when it is approved.'
          }
        }
      });
    } catch (err) {
      console.error('Error creating organization:', err);
      setError('Failed to create organization. Please try again.');
      setIsSubmitting(false);
    }
  };

  // ─── Progress Indicator ──────────────────────────────────────────

  const steps = currentUser
    ? [
      { key: 'church-plan', label: 'Choose Your Plan' },
      { key: 'org-details', label: 'Organization Details' },
    ]
    : [
      { key: 'org-name', label: 'Your Organization' },
      { key: 'personal-info', label: 'Your Account' },
      { key: 'church-plan', label: 'Choose Your Plan' },
      { key: 'org-details', label: 'Organization Details' },
    ];

  const currentStepIndex = steps.findIndex(s => s.key === step);

  const StepIndicator = () => {
    if (steps.length <= 1) return null;
    return (
      <div className="flex items-center justify-center mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className="flex items-center">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors ${
                  i < currentStepIndex
                    ? 'bg-green-500 text-white'
                    : i === currentStepIndex
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-500'
                }`}
              >
                {i < currentStepIndex ? (
                  <CheckCircleIcon className="h-5 w-5" />
                ) : (
                  i + 1
                )}
              </div>
              <span
                className={`ml-2 text-sm hidden sm:inline ${
                  i === currentStepIndex ? 'text-blue-600 font-semibold' : 'text-gray-500'
                }`}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div
                className={`w-8 sm:w-16 h-0.5 mx-2 ${
                  i < currentStepIndex ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            )}
          </React.Fragment>
        ))}
      </div>
    );
  };

  // ─── Loading state ───────────────────────────────────────────────

  if (step === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // ─── Step 1: Organization Name (unauthenticated only) ────────────

  if (step === 'org-name') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          <div className="bg-white shadow-lg rounded-2xl p-8">
            <StepIndicator />

            <div className="text-center mb-6">
              <BuildingOfficeIcon className="h-12 w-12 text-blue-500 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-gray-900">
                Let's Set Up Your Organization
              </h2>
              <p className="text-gray-500 mt-2">
                Start your discipleship journey by creating a home for your team.
              </p>
            </div>

            <form onSubmit={handleOrgNameNext} className="space-y-5">
              <div>
                <label htmlFor="orgName" className="block font-medium text-gray-700 mb-1">
                  Organization Name *
                </label>
                <input
                  type="text"
                  id="orgName"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Grace Community Church"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="displayName" className="block font-medium text-gray-700 mb-1">
                  Display Name
                  <span className="text-sm text-gray-400 ml-2">(optional, how it appears in the app)</span>
                </label>
                <input
                  type="text"
                  id="displayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={orgName || 'e.g. Grace Church'}
                />
              </div>

              {error && (
                <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</p>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
              >
                Continue
              </button>

              <p className="text-center text-sm text-gray-500 mt-4">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Log in
                </button>
              </p>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ─── Step 2: Personal Info & Account Creation ────────────────────

  if (step === 'personal-info') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="w-full max-w-lg">
          <div className="bg-white shadow-lg rounded-2xl p-8">
            <StepIndicator />

            <div className="text-center mb-6">
              <UserIcon className="h-12 w-12 text-blue-500 mx-auto mb-3" />
              <h2 className="text-2xl font-bold text-gray-900">
                Create Your Account
              </h2>
              <p className="text-gray-500 mt-2">
                You'll be the administrator of <span className="font-semibold text-gray-700">{orgName}</span>.
              </p>
            </div>

            <form onSubmit={handleCreateAccount} className="space-y-4">
              <div>
                <label htmlFor="personalName" className="block font-medium text-gray-700 mb-1">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="personalName"
                  value={personalName}
                  onChange={(e) => setPersonalName(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Your full name"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="personalEmail" className="block font-medium text-gray-700 mb-1">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="personalEmail"
                  value={personalEmail}
                  onChange={(e) => setPersonalEmail(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="personalPhone" className="block font-medium text-gray-700 mb-1">
                  Phone Number
                  <span className="text-sm text-gray-400 ml-2">(optional)</span>
                </label>
                <input
                  type="tel"
                  id="personalPhone"
                  value={personalPhone}
                  onChange={(e) => setPersonalPhone(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="password" className="block font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Re-enter your password"
                  required
                  minLength={6}
                />
              </div>

              {error && (
                <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 font-semibold rounded-lg transition ${
                  isSubmitting
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                    Creating your account...
                  </span>
                ) : (
                  'Create Account & Continue'
                )}
              </button>

              <button
                type="button"
                onClick={() => setStep('org-name')}
                className="w-full py-2 text-gray-500 text-sm hover:text-gray-700 transition"
              >
                &larr; Back
              </button>

              <p className="text-center text-sm text-gray-500">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-blue-600 hover:underline font-medium"
                >
                  Log in
                </button>
              </p>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ─── Step: Church Plan (Choose Your Plan) ──────────────────────────

  if (step === 'church-plan') {
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-4">
        <div className="w-full max-w-4xl">
          <div className="bg-white shadow-lg rounded-2xl p-8">
            <StepIndicator />

            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-gray-900">Choose Your Platform</h2>
              <p className="text-gray-500 mt-2">Starter or Standard. First 5 seats included — we help starter churches.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mb-8 max-w-xl mx-auto">
              {PLATFORM_TIERS.map((tier) => (
                <button
                  key={tier.id}
                  type="button"
                  onClick={() => setSelectedPlatformTier(tier.id)}
                  className={`p-6 rounded-xl border-2 text-left transition ${
                    selectedPlatformTier === tier.id
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <h3 className="font-bold text-gray-900">{tier.name}</h3>
                  <p className="text-sm text-blue-600 font-semibold mt-1">{tier.range}</p>
                  <p className="text-2xl font-extrabold text-gray-900 mt-3">${tier.priceMonthly}/mo</p>
                  <p className="text-xs text-gray-500">${tier.priceAnnual}/yr</p>
                </button>
              ))}
            </div>

            {error && (
              <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg mb-4">{error}</p>
            )}

            <button
              type="button"
              onClick={handleChurchPlanNext}
              disabled={!selectedPlatformTier}
              className={`w-full py-3 font-semibold rounded-lg transition ${
                selectedPlatformTier
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Continue
            </button>

            {!currentUser && (
              <button
                type="button"
                onClick={() => setStep('personal-info')}
                className="w-full py-2 text-gray-500 text-sm hover:text-gray-700 transition mt-2"
              >
                &larr; Back
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Final Step: Organization Details ─────────────────────────────

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl">
        <div className="bg-white shadow-lg rounded-2xl p-8">
          <StepIndicator />

          <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
            <BuildingOfficeIcon className="h-8 w-8 text-blue-500" />
            {currentUser && !steps.find(s => s.key === 'org-name')
              ? 'Application to Create Your Organization'
              : `Set Up ${orgName || 'Your Organization'}`}
          </h2>
          {steps.length > 1 && (
            <p className="text-gray-500 mb-6">
              Almost there! Fill in a few more details about your organization.
            </p>
          )}

          <form onSubmit={handleCreateOrg} className="space-y-6">
            {/* Org name fields — show for authenticated users (they skip org-name step) */}
            {currentUser && (
              <>
                <div>
                  <label htmlFor="orgName" className="block font-medium text-gray-700">Organization Name *</label>
                  <input
                    type="text"
                    id="orgName"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="displayName" className="block font-medium text-gray-700">
                    Display Name
                    <span className="text-sm text-gray-500 ml-2">(How it appears in the app)</span>
                  </label>
                  <input
                    type="text"
                    id="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                    placeholder={orgName}
                  />
                </div>
              </>
            )}

            <div>
              <label htmlFor="creatorName" className="block font-medium text-gray-700">Organization Creator</label>
              <input
                type="text"
                id="creatorName"
                value={userData?.name || personalName || currentUser?.email || 'Loading...'}
                className="w-full p-3 mt-1 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                disabled
              />
              <p className="mt-1 text-sm text-gray-500">You will be the administrator of this organization</p>
            </div>

            <div>
              <label htmlFor="bio" className="block font-medium text-gray-700">Organization Bio *</label>
              <textarea
                id="bio"
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={4}
                className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
                placeholder="Describe your organization's mission and purpose..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="country" className="block font-medium text-gray-700">Country *</label>
                <select
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select a country</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                </select>
              </div>

              <div>
                <label htmlFor="zipCode" className="block font-medium text-gray-700">Zip Code *</label>
                <input
                  type="text"
                  id="zipCode"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  required
                  placeholder="Enter zip code"
                />
              </div>
            </div>

            <div>
              <label htmlFor="website" className="block font-medium text-gray-700">
                Website
                <span className="text-sm text-gray-500 ml-2">(Optional)</span>
              </label>
              <input
                type="url"
                id="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                className="w-full p-3 mt-1 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://example.com"
              />
            </div>

            {/* Organization Settings */}
            <div className="mt-6 space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Organization Settings</h3>

              <div>
                <label htmlFor="primaryColor" className="block font-medium text-gray-700">Brand Color</label>
                <input
                  type="color"
                  id="primaryColor"
                  value={settings.primaryColor}
                  onChange={(e) => setSettings({...settings, primaryColor: e.target.value})}
                  className="h-10 w-20"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allowSelfJoin"
                  checked={settings.allowSelfJoin}
                  onChange={(e) => setSettings({...settings, allowSelfJoin: e.target.checked})}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="allowSelfJoin" className="ml-2 block text-sm text-gray-900">
                  Allow users to join organization without approval
                </label>
              </div>

              <div>
                <label htmlFor="defaultRole" className="block font-medium text-gray-700">Default Member Role</label>
                <select
                  id="defaultRole"
                  value={settings.defaultRole}
                  onChange={(e) => setSettings({...settings, defaultRole: e.target.value})}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-lg"
                >
                  <option value="Discipler">Discipler</option>
                  <option value="Mentor">Mentor</option>
                  <option value="Member">Member</option>
                </select>
              </div>

              <div className="space-y-2">
                <label className="block font-medium text-gray-700">Story Settings</label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="autoApproveStories"
                    checked={settings.storySettings.autoApprove}
                    onChange={(e) => setSettings({
                      ...settings,
                      storySettings: {
                        ...settings.storySettings,
                        autoApprove: e.target.checked
                      }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="autoApproveStories" className="ml-2 block text-sm text-gray-900">
                    Auto-approve member stories
                  </label>
                </div>
              </div>
            </div>

            {error && (
              <p className="text-red-600 text-sm bg-red-50 p-3 rounded-lg">{error}</p>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-3 text-white rounded-lg flex items-center gap-2 ${
                  isSubmitting
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit your Organization Application'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateOrganization;
